// Network status + offline sync queue manager.
//
// Detekuje online/offline pres @capacitor/network + browser events.
// Pri obnove pripojeni: pokud pending events > 0, spusti background sync.
// Exponential backoff per event (attempts^2 * 1000 ms cap 60s).

import { create } from "zustand";
import { Network } from "@capacitor/network";
import { sendEventsBatch, sendGpsBatch } from "../lib/api";
import {
  listPendingEvents, removePendingEvent, bumpAttempts, pendingEventsCount,
  listPendingGps, removePendingGps, bumpGpsAttempts, pendingGpsCount,
} from "../lib/db";

interface SyncState {
  online: boolean;
  pendingCount: number;
  syncing: boolean;
  lastSyncAt: number | null;
  lastError: string | null;

  /** Inicializace pri startu app — register network listeners. */
  init: () => Promise<() => void>;

  /** Spusti sync up vsech pending events. Idempotent (skip pokud uz bezi). */
  syncNow: () => Promise<void>;

  /** Refresh pending count z Dexie. */
  refreshCount: () => Promise<void>;
}

const MAX_BACKOFF_MS = 60_000;
const BACKOFF_BASE_MS = 1_000;

function backoffMsFor(attempts: number): number {
  return Math.min(MAX_BACKOFF_MS, BACKOFF_BASE_MS * Math.pow(2, attempts));
}

export const useSyncStore = create<SyncState>((set, get) => ({
  online: true,
  pendingCount: 0,
  syncing: false,
  lastSyncAt: null,
  lastError: null,

  init: async () => {
    // Initial state
    const status = await Network.getStatus();
    set({ online: status.connected });
    await get().refreshCount();

    // Capacitor listener
    const handle = await Network.addListener("networkStatusChange", (s) => {
      const wasOffline = !get().online;
      set({ online: s.connected });
      if (s.connected && wasOffline) {
        // Auto-sync pri obnove pripojeni
        void get().syncNow();
      }
    });

    // Periodicky retry (kazdych 30s pokud online a maji pending)
    const intervalId = setInterval(() => {
      const { online, pendingCount, syncing } = get();
      if (online && pendingCount > 0 && !syncing) {
        void get().syncNow();
      }
    }, 30_000);

    // Cleanup
    return () => {
      handle.remove();
      clearInterval(intervalId);
    };
  },

  refreshCount: async () => {
    const [evCount, gpsCount] = await Promise.all([
      pendingEventsCount(),
      pendingGpsCount(),
    ]);
    set({ pendingCount: evCount + gpsCount });
  },

  syncNow: async () => {
    const { syncing, online } = get();
    if (syncing) return;
    if (!online) return;

    set({ syncing: true, lastError: null });
    try {
      // Sync events
      while (true) {
        const events = await listPendingEvents(50);
        if (events.length === 0) break;
        const now = Date.now();
        // Backoff se měří od POSLEDNÍHO pokusu (ne od created_at) — jinak staré
        // záznamy mají now-created_at vždy > cap a přeposílají se každý cyklus.
        const ready = events.filter((e) => now - (e.last_attempt_at ?? e.created_at) >= backoffMsFor(e.attempts) - 1000);
        if (ready.length === 0) break;

        const entries = ready.map((e) => ({ shift_id: e.shift_id, event: e.payload }));
        try {
          const results = await sendEventsBatch(entries);
          for (const r of results) {
            if (r.status === "inserted" || r.status === "deduplicated") {
              await removePendingEvent(r.id);
            } else {
              await bumpAttempts(r.id, r.error ?? "rejected");
            }
          }
        } catch (e: any) {
          for (const ev of ready) await bumpAttempts(ev.id, e?.message ?? "batch failed");
          set({ lastError: e?.message ?? "Sync failed" });
          break;
        }
      }

      // Sync GPS positions (Sprint 6.2)
      while (true) {
        const positions = await listPendingGps(100);
        if (positions.length === 0) break;
        const now = Date.now();
        const ready = positions.filter((p) => now - (p.last_attempt_at ?? p.created_at) >= backoffMsFor(p.attempts) - 1000);
        if (ready.length === 0) break;

        const entries = ready.map((p) => ({
          id: p.id, shift_id: p.shift_id, ts: p.ts,
          lat: p.lat, lng: p.lng, accuracy: p.accuracy, battery: p.battery,
        }));
        try {
          const results = await sendGpsBatch(entries);
          for (const r of results) {
            if (r.status === "inserted" || r.status === "deduplicated") {
              await removePendingGps(r.id);
            } else {
              await bumpGpsAttempts(r.id, "rejected");
            }
          }
        } catch (e: any) {
          for (const p of ready) await bumpGpsAttempts(p.id, e?.message ?? "gps batch failed");
          set({ lastError: e?.message ?? "Sync failed" });
          break;
        }
      }

      set({ lastSyncAt: Date.now() });
    } finally {
      set({ syncing: false });
      await get().refreshCount();
    }
  },
}));
